import { apiClient } from "@/lib/api";
import type {
  CostSavingsGroupBy,
  CostSavingsReport,
  DashboardReport,
  FleetLoadReport,
  PaginatedDriverReport,
  PaginatedOrderReport,
  PaginatedRouteReport,
  PlanVsActualReport,
  ReportParams,
} from "@/types/reports";

/**
 * Fetches the dashboard KPI summary report.
 * GET /api/v1/reports/dashboard/
 */
export async function getDashboardReport(
  params: ReportParams,
): Promise<DashboardReport> {
  const response = await apiClient.get<DashboardReport>(
    "/v1/reports/dashboard/",
    { params },
  );
  return response.data;
}

/**
 * Fetches a paginated routes performance report.
 * GET /api/v1/reports/routes/
 */
export async function getRoutesReport(
  params: ReportParams & { page?: number },
): Promise<PaginatedRouteReport> {
  const response = await apiClient.get<PaginatedRouteReport>(
    "/v1/reports/routes/",
    { params },
  );
  return response.data;
}

/**
 * Fetches a paginated orders performance report.
 * GET /api/v1/reports/orders/
 */
export async function getOrdersReport(
  params: ReportParams & { page?: number },
): Promise<PaginatedOrderReport> {
  const response = await apiClient.get<PaginatedOrderReport>(
    "/v1/reports/orders/",
    { params },
  );
  return response.data;
}

/**
 * Exports the routes report as XLSX or PDF.
 * GET /api/v1/reports/routes/export/
 */
export async function exportRoutesReport(
  params: ReportParams & {
    format: "xlsx" | "pdf";
    lang?: string;
    columns?: string;
  },
): Promise<Blob> {
  const response = await apiClient.get("/v1/reports/routes/export/", {
    params,
    responseType: "blob",
  });
  return response.data as Blob;
}

/**
 * Exports the orders report as XLSX or PDF.
 * GET /api/v1/reports/orders/export/
 */
export async function exportOrdersReport(
  params: ReportParams & {
    format: "xlsx" | "pdf";
    lang?: string;
    columns?: string;
  },
): Promise<Blob> {
  const response = await apiClient.get("/v1/reports/orders/export/", {
    params,
    responseType: "blob",
  });
  return response.data as Blob;
}

/**
 * Fetches the per-driver performance scorecard (TASK-3729).
 * GET /api/v1/reports/drivers/
 */
export async function getDriversReport(
  params: ReportParams,
): Promise<PaginatedDriverReport> {
  const response = await apiClient.get<PaginatedDriverReport>(
    "/v1/reports/drivers/",
    { params },
  );
  return response.data;
}

/**
 * Exports the driver scorecard as XLSX or PDF (TASK-3729).
 * GET /api/v1/reports/drivers/export/
 */
export async function exportDriversReport(
  params: ReportParams & {
    format: "xlsx" | "pdf";
    lang?: string;
    columns?: string;
  },
): Promise<Blob> {
  const response = await apiClient.get("/v1/reports/drivers/export/", {
    params,
    responseType: "blob",
  });
  return response.data as Blob;
}

/**
 * Fetches the plan-vs-actual cost-savings report (TASK-3737).
 * GET /api/v1/reports/cost-savings/
 */
export async function getCostSavingsReport(
  params: ReportParams & { group_by: CostSavingsGroupBy },
): Promise<CostSavingsReport> {
  const response = await apiClient.get<CostSavingsReport>(
    "/v1/reports/cost-savings/",
    { params },
  );
  return response.data;
}

/**
 * Fetches the planned-vs-actual KPI report (TASK-3820).
 * GET /api/v1/reports/plan-vs-actual/
 */
export async function getPlanVsActualReport(
  params: ReportParams,
): Promise<PlanVsActualReport> {
  const response = await apiClient.get<PlanVsActualReport>(
    "/v1/reports/plan-vs-actual/",
    { params },
  );
  return response.data;
}

/**
 * Fetches the per-vehicle Fleet Load report (TASK-3821).
 * GET /api/v1/reports/fleet-load/
 */
export async function getFleetLoadReport(
  params: ReportParams,
): Promise<FleetLoadReport> {
  const response = await apiClient.get<FleetLoadReport>(
    "/v1/reports/fleet-load/",
    { params },
  );
  return response.data;
}

/**
 * Exports the Fleet Load report as XLSX or PDF (TASK-3821).
 * GET /api/v1/reports/fleet-load/export/
 */
export async function exportFleetLoadReport(
  params: ReportParams & {
    format: "xlsx" | "pdf";
    lang?: string;
    columns?: string;
  },
): Promise<Blob> {
  const response = await apiClient.get("/v1/reports/fleet-load/export/", {
    params,
    responseType: "blob",
  });
  return response.data as Blob;
}

/**
 * Exports the cost-savings (plan-vs-actual) report as XLSX or PDF (TASK-3823).
 * GET /api/v1/reports/cost-savings/export/
 */
export async function exportCostSavingsReport(
  params: ReportParams & {
    format: "xlsx" | "pdf";
    lang?: string;
    columns?: string;
  },
): Promise<Blob> {
  const response = await apiClient.get("/v1/reports/cost-savings/export/", {
    params,
    responseType: "blob",
  });
  return response.data as Blob;
}

/**
 * Exports the archived-orders (last 90 days) report as XLSX or PDF (TASK-3826).
 * GET /api/v1/reports/archived-orders/export/
 */
export async function exportArchivedOrdersReport(
  params: ReportParams & {
    format: "xlsx" | "pdf";
    lang?: string;
    columns?: string;
  },
): Promise<Blob> {
  const response = await apiClient.get("/v1/reports/archived-orders/export/", {
    params,
    responseType: "blob",
  });
  return response.data as Blob;
}
